import type { AnalysisOptions, SajuMode } from './types/input';

/**
 * SajuMode 프리셋을 해소하여 기본 옵션 객체를 반환한다.
 *
 * 반환된 객체는 사용자 명시 옵션보다 낮은 우선순위.
 * engine.ts에서 deep merge하여 명시 옵션이 프리셋을 override.
 */
export function resolvePreset(mode?: SajuMode): Partial<AnalysisOptions> {
  switch (mode) {
    case 'classic-ziping':
      return {
        strengthMode: 'score', // geuk-first는 추후 구현
        yongshinPriority: 'eokbu-first',
        sinsalEmphasis: 'low',
        useGyeokGukInFortune: true,
        useYongSinInFortune: true,
        // 격국: 보수적 자평
        preferRegularFormat: true,
        useAllHiddenStems: false,
        allowRestrainedGuk: false,
        // 합충: 보수적 (우선순위/통근약화 미적용)
        relationOptions: {
          requireAdjacency: true,
          blockContested: true,
          requireMonthSupport: true,
          applyClashWeakening: false,
          applyCombineWeakening: false,
          resolvePriority: false,
          markDormant: false,
        },
      };

    case 'jeongam-style':
      return {
        strengthMode: 'score',
        yongshinPriority: 'johou-first',
        sinsalEmphasis: 'high',
        useGyeokGukInFortune: true,
        useYongSinInFortune: true,
        // 격국: 적극적
        preferRegularFormat: false,
        useAllHiddenStems: true,
        allowRestrainedGuk: true,
        // 합충: 확장 (우선순위/잠재 활용)
        relationOptions: {
          requireAdjacency: true,
          blockContested: true,
          requireMonthSupport: true,
          applyClashWeakening: true,
          applyCombineWeakening: true,
          resolvePriority: true,
          markDormant: true,
        },
      };

    default:
      return {};
  }
}

/**
 * 프리셋 기본값에 사용자 명시 옵션을 merge한다.
 * 사용자가 명시적으로 지정한 옵션은 프리셋보다 우선.
 */
export function mergeWithPreset(
  options?: AnalysisOptions,
): AnalysisOptions {
  const preset = resolvePreset(options?.mode);
  if (!options) return preset as AnalysisOptions;

  // 1차: 프리셋 기본값
  const merged = { ...preset };

  // 2차: 사용자 명시 옵션으로 덮어쓰기 (undefined는 건너뜀)
  for (const [key, value] of Object.entries(options)) {
    if (value !== undefined && key !== 'mode') {
      if (key === 'relationOptions' && typeof value === 'object') {
        // relationOptions는 nested merge
        (merged as Record<string, unknown>)[key] = {
          ...((merged as Record<string, unknown>)[key] as Record<string, unknown> ?? {}),
          ...(value as Record<string, unknown>),
        };
      } else {
        (merged as Record<string, unknown>)[key] = value;
      }
    }
  }

  return merged as AnalysisOptions;
}
